import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * EL COMERCIO QUE SE ESTÁ MIRANDO.
 *
 * 🔴 Existe para que el filtro por comercio NO se escriba en 56 pantallas.
 *
 * Se midió: 56 pantallas de la consola consumen datos que ahora llevan comercio,
 * a través de 142 métodos del servicio. Escribir el filtro en cada una
 * garantiza que en un año esté puesto de cuatro formas y falte en algunas —
 * que es la enfermedad que este sistema ya tiene con la clasificación de
 * producto: 692 líneas, cuatro dialectos, y cuatro que fallan en silencio.
 *
 * Por eso: acá se guarda cuál es, y un interceptor lo agrega a cada petición.
 * Ninguna pantalla se entera.
 *
 * 📍 Se guarda el CÓDIGO, no el identificador interno: `?merchant=EPA` se lee en
 * la URL y en un log, y el back resuelve las dos formas.
 */
@Injectable({ providedIn: 'root' })
export class ComercioActivoService {

  /** La clave del navegador. Con prefijo para no chocar con lo que ya hay. */
  private static readonly CLAVE = 'mc_comercio_activo';

  /**
   * 🔴 `null` significa TODOS LOS COMERCIOS — la vista de CrediTotal — y NO es
   * lo mismo que MundoTotal. Confundirlos da números mal: lo que no tiene marca
   * es de MundoTotal, no es "todo".
   */
  private readonly _codigo = new BehaviorSubject<string | null>(null);

  /** Para que la barra superior y quien quiera se enteren del cambio. */
  readonly codigo$ = this._codigo.asObservable();

  constructor() {
    // Sobrevive a recargar la página: si no, se pierde en cada F5 y el operador
    // termina mirando otro comercio sin darse cuenta.
    try {
      const guardado = localStorage.getItem(ComercioActivoService.CLAVE);
      if (guardado) this._codigo.next(guardado);
    } catch { /* navegador sin almacenamiento: se arranca en "todos" */ }
  }

  get codigo(): string | null { return this._codigo.getValue(); }

  get esTodos(): boolean { return this._codigo.getValue() === null; }

  /** `null` vuelve a la vista de todos los comercios. */
  seleccionar(codigo: string | null): void {
    const limpio = codigo ? codigo.trim().toUpperCase() : null;
    if (limpio === this._codigo.getValue()) return;
    try {
      if (limpio) localStorage.setItem(ComercioActivoService.CLAVE, limpio);
      else localStorage.removeItem(ComercioActivoService.CLAVE);
    } catch { /* sin almacenamiento: vale solo para esta sesión */ }
    this._codigo.next(limpio);
  }

  limpiar(): void { this.seleccionar(null); }
}
